"use client";

import CssBaseline from "@mui/material/CssBaseline";
import MuiThemeProvider from "@mui/material/styles/ThemeProvider";
import responsiveFontSizes from "@mui/material/styles/responsiveFontSizes";
import createTheme from "@mui/material/styles/createTheme";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import merge from "lodash/merge";
import useSettings from "../hooks/useSettings";
import customThemeOptions from "./theme-options";
import NextAppDirEmotionCacheProvider from "./emotion-cache";

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    mobile: true; // thêm `mobile`
    tablet: true; // thêm `tablet`
    laptop: true; // thêm `laptop`
    desktop: true; // thêm `desktop`
  }
  interface Theme {
    salifyThemeKey?: string;
  }
  interface ThemeOptions {
    salifyThemeKey?: string;
  }
}

const ThemeProvider = ({
  theme,
  themeOptionsOverride,
  children,
}: React.PropsWithChildren<{
  theme?: string;
  /** From site builder `layout.theme` (e.g. palette / mui / themeOptions). */
  themeOptionsOverride?: Record<string, unknown>;
}>) => {
  const { settings } = useSettings();
  const themeOptions = customThemeOptions(theme);
  const mergedThemeOptions = merge(
    {},
    {
      ...themeOptions,
      direction: settings.direction,
    },
    themeOptionsOverride ?? {},
  );
  let themeValue = createTheme({
    ...(mergedThemeOptions as any),
    salifyThemeKey: theme ?? "DEFAULT",
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
        mobile: 0,
        tablet: 640,
        laptop: 1024,
        desktop: 1200,
      },
    },
  });
  themeValue = responsiveFontSizes(themeValue);
  themeValue.salifyThemeKey = theme ?? "DEFAULT";

  themeValue.shadows[1] = "0px 1px 3px rgba(3, 0, 71, 0.09)";
  themeValue.shadows[2] = "0px 4px 16px rgba(43, 52, 69, 0.1)";
  themeValue.shadows[3] = "0px 8px 45px rgba(3, 0, 71, 0.09)";
  themeValue.shadows[4] = "0px 0px 28px rgba(3, 0, 71, 0.01)";
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <NextAppDirEmotionCacheProvider
        options={{
          key: "css",
        }}
      >
        <MuiThemeProvider theme={themeValue}>
          <CssBaseline />
          {children}
        </MuiThemeProvider>
      </NextAppDirEmotionCacheProvider>
    </LocalizationProvider>
  );
};

export default ThemeProvider;
